import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Button,
  Snackbar,
  Tabs,
  Tab,
  TextField,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack,
  Description,
  Headphones,
  Lightbulb,
  Refresh,
  Edit,
  Save,
  Cancel,
  AutoAwesome,
  Home as HomeIcon,
  Map as MapIcon,
  Delete,
  Info as InfoIcon,
  Assessment as AssessmentIcon,
  History as HistoryIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Add,
  Share as ShareIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { createApiClient } from '../../utils/api';
import { formatReportMonth, formatDuration } from '../../utils/formatters';
import { useCurrentUser } from '../../hooks/useApi';
import { Navigate } from 'react-router-dom';
import { API_URL } from '../../utils/config';
import { MarketReportDropzone } from '../../components/admin/MarketReportDropzone';
import { RegenerateDescriptionDialog } from '../../components/admin/RegenerateDescriptionDialog';
import { MarketRepresentatives } from '../../components/admin/MarketRepresentatives';
import { SocialMediaContentGenerator } from '../../components/admin/SocialMediaContentGenerator';
import { GenerateMarketImageDialog } from '../../components/admin/GenerateMarketImageDialog';

// TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={{
        flex: 1,
        display: value === index ? 'flex' : 'none',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
      {...other}
    >
      {value === index && (
        <Box sx={{
          flex: 1,
          overflow: 'auto',
          p: 3,
          display: 'flex',
          flexDirection: 'column'
        }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export const MarketDetailAdmin: React.FC = () => {
  const { marketId } = useParams<{ marketId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const api = createApiClient(authenticatedFetch);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [monthTabValue, setMonthTabValue] = useState(0);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [auditLogDialogOpen, setAuditLogDialogOpen] = useState(false);
  const [selectedAuditEntry, setSelectedAuditEntry] = useState<any>(null);
  const [mainTabValue, setMainTabValue] = useState(0);
  const [addOfficeDialogOpen, setAddOfficeDialogOpen] = useState(false);
  const [editOfficeDialogOpen, setEditOfficeDialogOpen] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState<any>(null);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [newOffice, setNewOffice] = useState({
    address1: '',
    address2: '',
    city: '',
    state_province_region: '',
    postal_code: '',
    language_tag: 'en-US'
  });
  const [editOffice, setEditOffice] = useState({
    address1: '',
    address2: '',
    city: '',
    state_province_region: '',
    postal_code: '',
    language_tag: 'en-US'
  });
  const [generateImageDialogOpen, setGenerateImageDialogOpen] = useState(false);
  const [selectedImageType, setSelectedImageType] = useState<1 | 2 | 3>(1);

  // Reset selected month when market changes
  React.useEffect(() => {
    setSelectedMonth(null);
    setMonthTabValue(0);
  }, [marketId]);

  // Fetch all market data using the new comprehensive endpoint
  const { data: marketData, isLoading: loadingMarketData, error: marketDataError } = useQuery({
    queryKey: ['admin', 'market-all-data', marketId],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/markets/${marketId}/all`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch market data');
      }
      const result = await response.json();
      return result.data;
    },
    enabled: !!marketId
  });

  // Extract data from the combined response
  const market = marketData?.market;
  const reports = marketData?.reports?.map((r: any) => ({
    id: r.id,
    market_id: r.marketId,
    report_month: r.reportMonth,
    short_description: r.shortDescription,
    medium_description: r.mediumDescription,
    long_description: r.longDescription,
    is_published: r.isPublished,
    is_active: r.isActive,
    index_status: r.indexStatus,
    indexed_at: r.indexedAt,
    created_at: r.createdAt,
    updated_at: r.updatedAt,
    key_findings: r.keyFindings
  }));
  const podcasts = marketData?.podcasts?.map((p: any) => ({
    id: p.id,
    market_id: p.marketId,
    report_month: p.reportMonth,
    title: p.title,
    description: p.description,
    duration_seconds: p.durationSeconds,
    is_published: p.isPublished,
    is_active: p.isActive,
    created_at: p.createdAt,
    updated_at: p.updatedAt
  }));

  // Extract images from market object
  const images = {
    productImage: market?.productImage,
    productImage2: market?.productImage2,
    productImage3: market?.productImage3
  };
  
  // Extract all key findings from reports
  const keyFindings = React.useMemo(() => {
    if (!reports) return [];
    const allFindings: any[] = [];
    reports.forEach((report: any) => {
      if (report.key_findings && report.key_findings.length > 0) {
        report.key_findings.forEach((finding: any) => {
          allFindings.push({
            id: finding.id,
            report_month: report.report_month,
            display_order: finding.findingNumber,
            finding_text: finding.title,
            description: finding.description,
            created_at: finding.createdAt,
            updated_at: finding.updatedAt
          });
        });
      }
    });
    return allFindings;
  }, [reports]);

  // Fetch market audit log
  const { data: auditLogData } = useQuery({
    queryKey: ['admin', 'market-audit-log', marketId],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/markets/${marketId}/audit-log`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch audit log');
      }
      const result = await response.json();
      return result.data;
    },
    enabled: !!marketId
  });

  // Fetch offices for the market's country
  const { data: officesData, isLoading: loadingOffices } = useQuery({
    queryKey: ['admin', 'offices-by-country', market?.countryId],
    queryFn: async () => {
      if (!market?.countryId) return null;
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/offices/country/${market.countryId}`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch offices');
      }
      const result = await response.json();
      return result.data;
    },
    enabled: !!market?.countryId
  });

  // Mutation to update market description
  const updateDescriptionMutation = useMutation({
    mutationFn: async (description: string | null) => {
      const response = await api.put(`/api/marketcompass/admin/markets/${marketId}/update`, {
        description
      });
      return response.data;
    },
    onSuccess: (data) => {
      setSnackbarMessage('Market description updated successfully');
      setSnackbarOpen(true);
      setIsEditingDescription(false);
      // Invalidate market query to refresh data
      queryClient.invalidateQueries({ queryKey: ['admin', 'market-all-data', marketId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'market-audit-log', marketId] });
    },
    onError: (error: any) => {
      setSnackbarMessage(error.response?.data?.error || 'Failed to update market description');
      setSnackbarOpen(true);
    }
  });

  // Mutation to toggle market status
  const toggleMarketStatusMutation = useMutation({
    mutationFn: async (isActive: boolean) => {
      const response = await api.put(`/api/marketcompass/admin/markets/${marketId}/status`, {
        is_active: isActive
      });
      return response.data;
    },
    onSuccess: (data) => {
      setSnackbarMessage(data.message);
      setSnackbarOpen(true);
      // Invalidate market query to refresh data
      queryClient.invalidateQueries({ queryKey: ['admin', 'market-all-data', marketId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'markets'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'market-audit-log', marketId] });
    },
    onError: (error: any) => {
      setSnackbarMessage(error.response?.data?.error || 'Failed to update market status');
      setSnackbarOpen(true);
    }
  });

  // Mutation to delete key finding
  const deleteKeyFindingMutation = useMutation({
    mutationFn: async (keyFindingId: number) => {
      const response = await api.delete(`/api/marketcompass/admin/key-findings/${keyFindingId}/delete`);
      return response.data;
    },
    onSuccess: (data) => {
      setSnackbarMessage(data.message || 'Key finding deleted successfully');
      setSnackbarOpen(true);
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['admin', 'market-all-data', marketId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'market-audit-log', marketId] });
    },
    onError: (error: any) => {
      setSnackbarMessage(error.response?.data?.error || 'Failed to delete key finding');
      setSnackbarOpen(true);
    }
  });

  // Mutation to create office
  const createOfficeMutation = useMutation({
    mutationFn: async (officeData: any) => {
      const response = await api.post('/api/marketcompass/admin/offices/create', officeData);
      return response.data;
    },
    onSuccess: (data) => {
      setSnackbarMessage(data.message || 'Office created successfully');
      setSnackbarOpen(true);
      setAddOfficeDialogOpen(false);
      // Reset form
      setNewOffice({
        address1: '',
        address2: '',
        city: '',
        state_province_region: '',
        postal_code: '',
        language_tag: 'en-US'
      });
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['admin', 'offices-by-country', market?.countryId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'market-audit-log', marketId] });
    },
    onError: (error: any) => {
      setSnackbarMessage(error.response?.data?.error || 'Failed to create office');
      setSnackbarOpen(true);
    }
  });

  // Mutation to update office
  const updateOfficeMutation = useMutation({
    mutationFn: async ({ officeId, officeData }: { officeId: number; officeData: any }) => {
      const response = await api.put(`/api/marketcompass/admin/offices/${officeId}`, officeData);
      return response.data;
    },
    onSuccess: (data) => {
      setSnackbarMessage(data.message || 'Office updated successfully');
      setSnackbarOpen(true);
      setEditOfficeDialogOpen(false);
      setSelectedOffice(null);
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['admin', 'offices-by-country', market?.countryId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'market-audit-log', marketId] });
    },
    onError: (error: any) => {
      setSnackbarMessage(error.response?.data?.error || 'Failed to update office');
      setSnackbarOpen(true);
    }
  });

  const reportColumns: GridColDef[] = [
    {
      field: 'report_month',
      headerName: 'Month',
      width: 150,
      renderCell: (params) => formatReportMonth(params.value)
    },
    {
      field: 'short_description',
      headerName: 'Description',
      flex: 1,
      minWidth: 300
    },
    {
      field: 'is_published',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Published' : 'Draft'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'index_status',
      headerName: 'Indexing Status',
      width: 140,
      renderCell: (params) => {
        if (!params.value) {
          return <Chip label="Not Indexed" color="default" size="small" />;
        }
        const status = params.value as string;
        const color = status === 'indexed' ? 'success' : 'warning';
        const label = status.charAt(0).toUpperCase() + status.slice(1);
        return <Chip label={label} color={color} size="small" />;
      }
    },
    {
      field: 'indexed_at',
      headerName: 'Indexed At',
      width: 120,
      valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : '-'
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueFormatter: (value) => new Date(value).toLocaleDateString()
    }
  ];

  const podcastColumns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 300
    },
    {
      field: 'duration_seconds',
      headerName: 'Duration',
      width: 100,
      renderCell: (params) => formatDuration(params.value)
    },
    {
      field: 'is_published',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Published' : 'Draft'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueFormatter: (value) => new Date(value).toLocaleDateString()
    }
  ];

  const keyFindingColumns: GridColDef[] = [
    {
      field: 'display_order',
      headerName: '#',
      width: 60,
      type: 'number'
    },
    {
      field: 'finding_text',
      headerName: 'Key Finding',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'medium', whiteSpace: 'normal', wordWrap: 'break-word' }}>
            {params.value}
          </Typography>
          {params.row.description && params.row.description !== params.value && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, whiteSpace: 'normal', wordWrap: 'break-word' }}>
              {params.row.description}
            </Typography>
          )}
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm('Are you sure you want to delete this key finding?')) {
              deleteKeyFindingMutation.mutate(params.row.id);
            }
          }}
          disabled={deleteKeyFindingMutation.isPending}
        >
          <Delete fontSize="small" />
        </IconButton>
      )
    }
  ];

  const auditLogColumns: GridColDef[] = [
    {
      field: 'created_at',
      headerName: 'Date/Time',
      width: 180,
      renderCell: (params) => new Date(params.value).toLocaleString()
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 200
    },
    {
      field: 'table_name',
      headerName: 'Table',
      width: 200
    },
    {
      field: 'performed_by',
      headerName: 'Performed By',
      width: 250
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 1,
      minWidth: 300,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word', py: 1 }}>
          {params.value}
        </Box>
      )
    }
  ];

  const handleRefresh = () => {
    // Invalidate the single comprehensive query to refresh all data
    queryClient.invalidateQueries({ queryKey: ['admin', 'market-all-data', marketId] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'market-audit-log', marketId] });
  };

  const handleEditDescription = () => {
    setEditedDescription(market?.description || '');
    setIsEditingDescription(true);
  };

  const handleSaveDescription = () => {
    const trimmedDescription = editedDescription.trim();
    const descriptionToSave = trimmedDescription === '' ? null : trimmedDescription;
    updateDescriptionMutation.mutate(descriptionToSave);
  };

  const handleCancelEdit = () => {
    setIsEditingDescription(false);
    setEditedDescription('');
  };

  const handleCreateOffice = () => {
    if (!market?.countryId) return;

    createOfficeMutation.mutate({
      country_id: market.countryId,
      market_id: parseInt(marketId!),
      ...newOffice
    });
  };

  const handleEditOffice = (office: any) => {
    setSelectedOffice(office);
    setEditOffice({
      address1: office.address1 || '',
      address2: office.address2 || '',
      city: office.city || '',
      state_province_region: office.state_province_region || '',
      postal_code: office.postal_code || '',
      language_tag: office.language_tag || 'en-US'
    });
    setEditOfficeDialogOpen(true);
  };

  const handleUpdateOffice = () => {
    if (!selectedOffice) return;

    updateOfficeMutation.mutate({
      officeId: selectedOffice.id,
      officeData: {
        market_id: parseInt(marketId!),
        ...editOffice
      }
    });
  };

  const handleActivateClick = () => {
    if (!market?.isActive) {
      // Show confirmation dialog for activation
      setActivateDialogOpen(true);
    } else {
      // Show confirmation dialog for deactivation
      setDeactivateDialogOpen(true);
    }
  };

  const handleConfirmActivate = () => {
    toggleMarketStatusMutation.mutate(true);
    setActivateDialogOpen(false);
  };

  const handleConfirmDeactivate = () => {
    toggleMarketStatusMutation.mutate(false);
    setDeactivateDialogOpen(false);
  };

  const handleOpenGenerateDialog = (imageType: 1 | 2 | 3) => {
    setSelectedImageType(imageType);
    setGenerateImageDialogOpen(true);
  };

  const handleImageGenerationSuccess = () => {
    setSnackbarMessage('Image generated and saved successfully');
    setSnackbarOpen(true);
    // Refresh market data to show the new image
    queryClient.invalidateQueries({ queryKey: ['admin', 'market-all-data', marketId] });
  };

  // Group all data by report month
  const dataByMonth = React.useMemo(() => {
    const months = new Set<number>();
    
    // Collect all months from reports
    reports?.forEach((report: any) => {
      if (report.report_month) months.add(report.report_month);
    });
    
    // Collect all months from podcasts
    podcasts?.forEach((podcast: any) => {
      if (podcast.report_month) months.add(podcast.report_month);
    });
    
    // Sort months in descending order
    const sortedMonths = Array.from(months).sort((a, b) => b - a);
    
    // Create data structure for each month
    const result: { [key: string]: { reports: any[], podcasts: any[], keyFindings: any[] } } = {};
    
    sortedMonths.forEach(month => {
      result[month.toString()] = {
        reports: reports?.filter((r: any) => r.report_month === month) || [],
        podcasts: podcasts?.filter((p: any) => p.report_month === month) || [],
        keyFindings: keyFindings?.filter((kf: any) => kf.report_month === month) || []
      };
    });
    
    return result;
  }, [reports, podcasts, keyFindings]);

  // Get months sorted in reverse chronological order (newest first)
  const reportMonths = React.useMemo(() => {
    return Object.keys(dataByMonth).sort((a, b) => parseInt(b) - parseInt(a));
  }, [dataByMonth]);

  // Set initial selected month (most recent)
  React.useEffect(() => {
    if (reportMonths.length > 0 && !selectedMonth) {
      setSelectedMonth(reportMonths[0]);
    }
  }, [reportMonths, selectedMonth]);

  // Calculate loading state
  const isLoading = loadingMarketData;

  // Redirect if user doesn't have admin or customize permission
  if (!user?.permissions?.includes('market_compass.admin') && !user?.permissions?.includes('market_compass.customize')) {
    return <Navigate to="/" replace />;
  }

  // Show loading state
  if (isLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading market details...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Handle market not found
  if (!market) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ my: 4 }}>
          Market not found.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumb */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            color="inherit"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/admin/hub')}
          >
            Admin Hub
          </Link>
          <Link
            color="inherit"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/admin/manage-markets')}
          >
            Market Management
          </Link>
          <Typography color="text.primary">{market?.name}</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <MapIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              {market?.name} - Market Details
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage reports, podcasts, and key findings for this market
          </Typography>
        </Box>

        {/* Main Tabs */}
        <Paper sx={{
          mb: 4,
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 320px)', // Account for header, breadcrumbs, and page title
          minHeight: '600px' // Reasonable minimum height
        }}>
          <Tabs
            value={mainTabValue}
            onChange={(e, newValue) => setMainTabValue(newValue)}
            aria-label="market detail tabs"
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2, flexShrink: 0 }}
          >
            <Tab icon={<InfoIcon />} iconPosition="start" label="Information" {...a11yProps(0)} />
            <Tab icon={<AssessmentIcon />} iconPosition="start" label="Assets" {...a11yProps(1)} />
            <Tab icon={<ImageIcon />} iconPosition="start" label="Images" {...a11yProps(2)} />
            <Tab icon={<BusinessIcon />} iconPosition="start" label="Offices" {...a11yProps(3)} />
            <Tab icon={<PeopleIcon />} iconPosition="start" label="Representatives" {...a11yProps(4)} />
            <Tab icon={<ShareIcon />} iconPosition="start" label="Social Media" {...a11yProps(5)} />
            <Tab icon={<HistoryIcon />} iconPosition="start" label="Audit Log" {...a11yProps(6)} />
          </Tabs>

          {/* Market Information Tab */}
          <TabPanel value={mainTabValue} index={0}>
            <Card sx={{ position: 'relative', backgroundColor: 'white', boxShadow: 0, height: '100%' }}>
              <IconButton
                onClick={handleRefresh}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1
                }}
              >
                <Refresh />
              </IconButton>
              <CardContent sx={{ height: '100%', overflow: 'auto' }}>
            <Typography variant="h5" gutterBottom>
              Market Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Continent
                </Typography>
                <Typography variant="h6">
                  {market.continentName || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Country
                </Typography>
                <Typography variant="h6">
                  {market.countryName || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Market Name
                </Typography>
                <Typography variant="h6">
                  {market.name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ textAlign: 'right' }}>
                  Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5, justifyContent: 'flex-end' }}>
                  <Chip
                    label={market.isActive ? 'Active' : 'Inactive'}
                    color={market.isActive ? 'success' : 'default'}
                    size="medium"
                  />
                  <Button
                    variant={market.isActive ? 'outlined' : 'contained'}
                    size="small"
                    onClick={handleActivateClick}
                    disabled={toggleMarketStatusMutation.isPending}
                    sx={market.isActive ? {
                      backgroundColor: 'transparent',
                      color: '#003F9F',
                      border: '2px solid #003F9F',
                      borderRadius: '3px',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 63, 159, 0.04)',
                        border: '2px solid #003F9F'
                      }
                    } : {
                      backgroundColor: '#F5A800',
                      color: '#000000',
                      fontWeight: 600,
                      borderRadius: '3px',
                      border: 'none',
                      '&:hover': {
                        backgroundColor: '#D89400'
                      }
                    }}
                  >
                    {market.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Alert severity="success">
                  If the market has been activated, this information is visible on the HTLMarketCompass website when users are exploring the available reports.
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Description
                  </Typography>
                  {isEditingDescription ? (
                    <Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={12}
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        placeholder="Enter market description..."
                        variant="outlined"
                        size="small"
                        inputProps={{ maxLength: 5000 }}
                        helperText={`${editedDescription.length}/5000 characters`}
                        sx={{ mb: 2 }}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Save />}
                          onClick={handleSaveDescription}
                          disabled={updateDescriptionMutation.isPending}
                          sx={{
                            backgroundColor: '#32373C',
                            color: '#FFFFFF',
                            padding: '0.75em 1.5em',
                            borderRadius: '3px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            '&:hover': {
                              backgroundColor: '#1f2327'
                            }
                          }}
                        >
                          Save
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Cancel />}
                          onClick={handleCancelEdit}
                          disabled={updateDescriptionMutation.isPending}
                          sx={{
                            backgroundColor: 'transparent',
                            color: '#003F9F',
                            border: '2px solid #003F9F',
                            borderRadius: '3px',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 63, 159, 0.04)',
                              border: '2px solid #003F9F'
                            }
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
                        {market.description || <em style={{ color: 'text.secondary' }}>No description available</em>}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Edit />}
                          onClick={handleEditDescription}
                          sx={{
                            backgroundColor: 'transparent',
                            color: '#003F9F',
                            border: '2px solid #003F9F',
                            borderRadius: '3px',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 63, 159, 0.04)',
                              border: '2px solid #003F9F'
                            }
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AutoAwesome />}
                          onClick={() => setRegenerateDialogOpen(true)}
                          sx={{
                            backgroundColor: '#32373C',
                            color: '#FFFFFF',
                            padding: '0.75em 1.5em',
                            borderRadius: '3px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            '&:hover': {
                              backgroundColor: '#1f2327'
                            }
                          }}
                        >
                          Regenerate Description
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
          </TabPanel>

          {/* Market Assets Tab */}
          <TabPanel value={mainTabValue} index={1}>
            <Paper sx={{ position: 'relative', backgroundColor: 'white', boxShadow: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Upload Report Button - Upper Right - Visible to market_compass.admin and market_compass.customize users */}
          {(user?.permissions?.includes('market_compass.admin') || user?.permissions?.includes('market_compass.customize')) && (
            <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
              <MarketReportDropzone
                marketId={parseInt(marketId!)}
                marketName={market.name}
                onProcessingComplete={handleRefresh}
                compact={true}
                goldBranding={true}
              />
            </Box>
          )}

          <Box sx={{ p: 3, pb: 0, flexShrink: 0 }}>
            <Typography variant="h5" gutterBottom>
              Market Assets
            </Typography>
            <Alert severity="warning" sx={{ mt: '10px', mb: 2 }}>
              The information below is only visible after the report has been purchased.
            </Alert>
          </Box>

          {reportMonths.length > 0 ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Month selection chips */}
              <Box sx={{
                px: 3,
                py: 2,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                alignItems: 'center'
              }}>
                {reportMonths.map((month) => (
                  <Chip
                    key={month}
                    label={formatReportMonth(parseInt(month))}
                    onClick={() => {
                      setSelectedMonth(month);
                      setMonthTabValue(0); // Reset to first tab when changing month
                    }}
                    color={selectedMonth === month ? 'primary' : 'default'}
                    variant={selectedMonth === month ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>

              {/* Secondary tabs for content types within selected month */}
              {selectedMonth && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50', px: 3, flexShrink: 0 }}>
                    <Tabs
                      value={monthTabValue}
                      onChange={(e, newValue) => setMonthTabValue(newValue)}
                      aria-label="content type tabs"
                    >
                      <Tab 
                        icon={<Description />} 
                        iconPosition="start" 
                        label={`Reports (${dataByMonth[selectedMonth]?.reports?.length || 0})`} 
                        {...a11yProps(0)} 
                      />
                      <Tab 
                        icon={<Lightbulb />} 
                        iconPosition="start" 
                        label={`Key Findings (${dataByMonth[selectedMonth]?.keyFindings?.length || 0})`} 
                        {...a11yProps(1)} 
                      />
                      <Tab 
                        icon={<Headphones />} 
                        iconPosition="start" 
                        label={`Podcasts (${dataByMonth[selectedMonth]?.podcasts?.length || 0})`} 
                        {...a11yProps(2)} 
                      />
                    </Tabs>
                  </Box>

                  {/* Reports Tab */}
                  <TabPanel value={monthTabValue} index={0}>
                    <Box sx={{ flex: 1, px: 3, pb: 3, minHeight: 400 }}>
                      {dataByMonth[selectedMonth]?.reports?.length > 0 ? (
                        <DataGrid
                          rows={dataByMonth[selectedMonth].reports}
                          columns={reportColumns}
                          getRowId={(row) => row.id}
                          pageSizeOptions={[10, 25, 50]}
                          initialState={{
                            pagination: {
                              paginationModel: { pageSize: 10 }
                            }
                          }}
                          disableRowSelectionOnClick
                          onRowClick={(params) => {
                            navigate(`/admin/hub/marketcompass/markets/${marketId}/report/${params.row.id}`);
                          }}
                          sx={{
                            '& .MuiDataGrid-row': {
                              cursor: 'pointer'
                            }
                          }}
                        />
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body1" color="text.secondary">
                            No reports available for {formatReportMonth(parseInt(selectedMonth))}.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TabPanel>

                  {/* Key Findings Tab */}
                  <TabPanel value={monthTabValue} index={1}>
                    <Box sx={{ flex: 1, px: 3, pb: 3, minHeight: 400 }}>
                      {dataByMonth[selectedMonth]?.keyFindings?.length > 0 ? (
                        <DataGrid
                          rows={dataByMonth[selectedMonth].keyFindings}
                          columns={keyFindingColumns}
                          getRowId={(row) => row.id}
                          getRowHeight={() => 'auto'}
                          pageSizeOptions={[10, 25, 50]}
                          initialState={{
                            pagination: {
                              paginationModel: { pageSize: 10 }
                            },
                            sorting: {
                              sortModel: [{ field: 'display_order', sort: 'asc' }]
                            }
                          }}
                          disableRowSelectionOnClick
                          onRowClick={(params) => {
                            navigate(`/admin/hub/marketcompass/markets/${marketId}/key-finding/${params.row.id}`);
                          }}
                          sx={{
                            '& .MuiDataGrid-row': {
                              cursor: 'pointer'
                            },
                            '& .MuiDataGrid-cell': {
                              py: 2
                            }
                          }}
                        />
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body1" color="text.secondary">
                            No key findings available for {formatReportMonth(parseInt(selectedMonth))}.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TabPanel>

                  {/* Podcasts Tab */}
                  <TabPanel value={monthTabValue} index={2}>
                    <Box sx={{ flex: 1, px: 3, pb: 3, minHeight: 400 }}>
                      {dataByMonth[selectedMonth]?.podcasts?.length > 0 ? (
                        <DataGrid
                          rows={dataByMonth[selectedMonth].podcasts}
                          columns={podcastColumns}
                          getRowId={(row) => row.id}
                          pageSizeOptions={[10, 25, 50]}
                          initialState={{
                            pagination: {
                              paginationModel: { pageSize: 10 }
                            }
                          }}
                          disableRowSelectionOnClick
                          onRowClick={(params) => {
                            navigate(`/admin/hub/marketcompass/markets/${marketId}/podcast/${params.row.id}`);
                          }}
                          sx={{
                            '& .MuiDataGrid-row': {
                              cursor: 'pointer'
                            }
                          }}
                        />
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body1" color="text.secondary">
                            No podcasts available for {formatReportMonth(parseInt(selectedMonth))}.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TabPanel>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No content available
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Drop a MarketCompass report above to get started.
              </Typography>
            </Box>
          )}
        </Paper>
          </TabPanel>

          {/* Images Tab */}
          <TabPanel value={mainTabValue} index={2}>
            <Paper sx={{ backgroundColor: 'white', boxShadow: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 3, flex: 1 }}>
                <Typography variant="h5" gutterBottom>
                  Market Images
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Images used for market products and customization
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Product Image 1
                        </Typography>
                        {images.productImage ? (
                          <Box
                            component="img"
                            src={images.productImage}
                            alt="Product Image 1"
                            sx={{
                              width: '100%',
                              height: 'auto',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              mb: 2
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: 200,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'grey.100',
                              borderRadius: 1,
                              mb: 2
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              No image
                            </Typography>
                          </Box>
                        )}
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<AutoAwesome />}
                          onClick={() => handleOpenGenerateDialog(1)}
                          sx={{
                            bgcolor: '#F5A800',
                            color: '#000000',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#D89500' }
                          }}
                        >
                          Generate New Image
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Product Image 2
                        </Typography>
                        {images.productImage2 ? (
                          <Box
                            component="img"
                            src={images.productImage2}
                            alt="Product Image 2"
                            sx={{
                              width: '100%',
                              height: 'auto',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              mb: 2
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: 200,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'grey.100',
                              borderRadius: 1,
                              mb: 2
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              No image
                            </Typography>
                          </Box>
                        )}
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<AutoAwesome />}
                          onClick={() => handleOpenGenerateDialog(2)}
                          sx={{
                            bgcolor: '#F5A800',
                            color: '#000000',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#D89500' }
                          }}
                        >
                          Generate New Image
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Product Image 3
                        </Typography>
                        {images.productImage3 ? (
                          <Box
                            component="img"
                            src={images.productImage3}
                            alt="Product Image 3"
                            sx={{
                              width: '100%',
                              height: 'auto',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              mb: 2
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: 200,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'grey.100',
                              borderRadius: 1,
                              mb: 2
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              No image
                            </Typography>
                          </Box>
                        )}
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<AutoAwesome />}
                          onClick={() => handleOpenGenerateDialog(3)}
                          sx={{
                            bgcolor: '#F5A800',
                            color: '#000000',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#D89500' }
                          }}
                        >
                          Generate New Image
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </TabPanel>

          {/* Offices Tab */}
          <TabPanel value={mainTabValue} index={3}>
            <Paper sx={{ backgroundColor: 'white', boxShadow: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5">
                    Horwath HTL Offices in {market?.countryName}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setAddOfficeDialogOpen(true)}
                    sx={{
                      backgroundColor: '#32373C',
                      color: '#FFFFFF',
                      '&:hover': { backgroundColor: '#1f2327' }
                    }}
                  >
                    Add Office
                  </Button>
                </Box>

                {loadingOffices ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body1" sx={{ mt: 2 }}>Loading offices...</Typography>
                  </Box>
                ) : officesData && officesData.offices && officesData.offices.length > 0 ? (
                  <Grid container spacing={3} sx={{ mt: 1 }}>
                    {officesData.offices.map((office: any) => (
                      <Grid item xs={12} sm={6} md={4} key={office.id}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditOffice(office)}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              zIndex: 1
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <CardContent sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                              <Typography variant="h6" component="div">
                                {office.city}
                                {office.state_province_region && `, ${office.state_province_region}`}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {office.address1 && (
                                <Typography variant="body2" color="text.secondary">
                                  {office.address1}
                                </Typography>
                              )}
                              {office.address2 && (
                                <Typography variant="body2" color="text.secondary">
                                  {office.address2}
                                </Typography>
                              )}
                              {office.postal_code && (
                                <Typography variant="body2" color="text.secondary">
                                  {office.postal_code}
                                </Typography>
                              )}
                              {office.country_name && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 'medium' }}>
                                  {office.country_name}
                                </Typography>
                              )}
                              {(office.latitude && office.longitude) && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                  Coordinates: {office.latitude}, {office.longitude}
                                </Typography>
                              )}
                              {office.language_tag && (
                                <Chip
                                  label={`Language: ${office.language_tag}`}
                                  size="small"
                                  sx={{ mt: 1, alignSelf: 'flex-start' }}
                                />
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No offices found in {market?.countryName}.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </TabPanel>

          {/* Representatives Tab */}
          <TabPanel value={mainTabValue} index={4}>
            <Box sx={{ height: '100%', overflow: 'auto' }}>
              <MarketRepresentatives
                marketId={parseInt(marketId!)}
                marketName={market?.name || ''}
                onUpdate={() => {
                  queryClient.invalidateQueries({ queryKey: ['admin', 'market-audit-log', marketId] });
                }}
              />
            </Box>
          </TabPanel>

          {/* Social Media Tab */}
          <TabPanel value={mainTabValue} index={5}>
            <Box sx={{ height: '100%', overflow: 'auto' }}>
              <SocialMediaContentGenerator
                marketId={marketId!}
                marketData={market}
              />
            </Box>
          </TabPanel>

          {/* Market Audit Log Tab */}
          <TabPanel value={mainTabValue} index={6}>
            <Paper sx={{ backgroundColor: 'white', boxShadow: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h5" gutterBottom>
              Market Audit Log
            </Typography>
            <Box sx={{ flex: 1, mt: 2 }}>
              {auditLogData && auditLogData.length > 0 ? (
                <DataGrid
                  rows={auditLogData}
                  columns={auditLogColumns}
                  getRowId={(row) => row.id}
                  getRowHeight={() => 'auto'}
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 10 }
                    }
                  }}
                  disableRowSelectionOnClick
                  onRowClick={(params) => {
                    setSelectedAuditEntry(params.row);
                    setAuditLogDialogOpen(true);
                  }}
                  sx={{
                    '& .MuiDataGrid-row': {
                      cursor: 'pointer'
                    }
                  }}
                />
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No audit log entries found.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
          </TabPanel>
        </Paper>
      </Box>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Regenerate Description Dialog */}
      {market && (
        <RegenerateDescriptionDialog
          open={regenerateDialogOpen}
          onClose={() => setRegenerateDialogOpen(false)}
          marketId={parseInt(marketId!)}
          marketName={market.name}
          onSuccess={() => {
            setSnackbarMessage('Market description regenerated successfully');
            setSnackbarOpen(true);
            // Refresh market data
            queryClient.invalidateQueries({ queryKey: ['admin', 'market-all-data', marketId] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'market-audit-log', marketId] });
          }}
        />
      )}

      {/* Audit Log Details Dialog */}
      <Dialog
        open={auditLogDialogOpen}
        onClose={() => setAuditLogDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Audit Log Entry Details</DialogTitle>
        <DialogContent>
          {selectedAuditEntry && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Date/Time
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedAuditEntry.created_at).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Action
                  </Typography>
                  <Typography variant="body1">
                    {selectedAuditEntry.action}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Table
                  </Typography>
                  <Typography variant="body1">
                    {selectedAuditEntry.table_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Entity ID
                  </Typography>
                  <Typography variant="body1">
                    {selectedAuditEntry.entity_id || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Performed By
                  </Typography>
                  <Typography variant="body1">
                    {selectedAuditEntry.performed_by}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Notes
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedAuditEntry.notes || 'No notes'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditLogDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Office Dialog */}
      <Dialog
        open={addOfficeDialogOpen}
        onClose={() => setAddOfficeDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Office</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Alert severity="info">
                Creating a new office in {market?.countryName}
              </Alert>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={market?.countryName || ''}
                disabled
                helperText="Pre-populated from market"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Language Tag"
                value={newOffice.language_tag}
                disabled
                helperText="Pre-populated (e.g., en-US)"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={newOffice.address1}
                onChange={(e) => setNewOffice({ ...newOffice, address1: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={newOffice.address2}
                onChange={(e) => setNewOffice({ ...newOffice, address2: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={newOffice.city}
                onChange={(e) => setNewOffice({ ...newOffice, city: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State/Province/Region"
                value={newOffice.state_province_region}
                onChange={(e) => setNewOffice({ ...newOffice, state_province_region: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Postal Code"
                value={newOffice.postal_code}
                onChange={(e) => setNewOffice({ ...newOffice, postal_code: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOfficeDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateOffice}
            variant="contained"
            disabled={createOfficeMutation.isPending || !newOffice.city}
            sx={{
              backgroundColor: '#32373C',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#1f2327' }
            }}
          >
            {createOfficeMutation.isPending ? 'Creating...' : 'Create Office'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Office Dialog */}
      <Dialog
        open={editOfficeDialogOpen}
        onClose={() => setEditOfficeDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Office</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Alert severity="info">
                Editing office in {selectedOffice?.country_name}
              </Alert>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={selectedOffice?.country_name || ''}
                disabled
                helperText="Cannot be changed"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Language Tag"
                value={editOffice.language_tag}
                onChange={(e) => setEditOffice({ ...editOffice, language_tag: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={editOffice.address1}
                onChange={(e) => setEditOffice({ ...editOffice, address1: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={editOffice.address2}
                onChange={(e) => setEditOffice({ ...editOffice, address2: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={editOffice.city}
                onChange={(e) => setEditOffice({ ...editOffice, city: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State/Province/Region"
                value={editOffice.state_province_region}
                onChange={(e) => setEditOffice({ ...editOffice, state_province_region: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Postal Code"
                value={editOffice.postal_code}
                onChange={(e) => setEditOffice({ ...editOffice, postal_code: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOfficeDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateOffice}
            variant="contained"
            disabled={updateOfficeMutation.isPending || !editOffice.city}
            sx={{
              backgroundColor: '#32373C',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#1f2327' }
            }}
          >
            {updateOfficeMutation.isPending ? 'Updating...' : 'Update Office'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activate Market Confirmation Dialog */}
      <Dialog
        open={activateDialogOpen}
        onClose={() => setActivateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="warning" />
          Activate Market on HTL MarketCompass
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" paragraph>
              You are about to activate <strong>{market?.name}</strong> on the HTL MarketCompass website.
            </Typography>
            <Typography variant="body1" paragraph>
              Once activated, this market and its associated reports will be <strong>publicly visible and available for purchase</strong> to customers worldwide on the HTL MarketCompass platform.
            </Typography>
            <Alert severity="info" sx={{ mt: 2 }}>
              You can deactivate this market at any time to remove it from public visibility while maintaining all existing data and customer access.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setActivateDialogOpen(false)}
            sx={{
              color: '#003F9F',
              '&:hover': {
                backgroundColor: 'rgba(0, 63, 159, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmActivate}
            variant="contained"
            disabled={toggleMarketStatusMutation.isPending}
            sx={{
              backgroundColor: '#F5A800',
              color: '#000000',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#D89400' }
            }}
          >
            {toggleMarketStatusMutation.isPending ? 'Activating...' : 'Activate Market'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Market Confirmation Dialog */}
      <Dialog
        open={deactivateDialogOpen}
        onClose={() => setDeactivateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="info" />
          Deactivate Market on HTL MarketCompass
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" paragraph>
              You are about to deactivate <strong>{market?.name}</strong> on the HTL MarketCompass website.
            </Typography>
            <Typography variant="body1" paragraph>
              Once deactivated, this market will be <strong>removed from public view</strong> on the HTL MarketCompass platform. New customers will no longer be able to discover or purchase reports for this market.
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" paragraph sx={{ mb: 1 }}>
                <strong>Important:</strong> Deactivating this market will:
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <li>
                  <Typography variant="body2">
                    Hide the market from browse and search results
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Prevent new purchases of this market's reports
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Remove product listings from the marketplace
                  </Typography>
                </li>
              </Box>
            </Alert>
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2" paragraph sx={{ mb: 1 }}>
                <strong>Existing customers retain access:</strong>
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <li>
                  <Typography variant="body2">
                    Customers with active subscriptions will continue to have full access
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    All historical data and reports will be preserved
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    You can reactivate this market at any time
                  </Typography>
                </li>
              </Box>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeactivateDialogOpen(false)}
            sx={{
              color: '#003F9F',
              '&:hover': {
                backgroundColor: 'rgba(0, 63, 159, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeactivate}
            variant="contained"
            disabled={toggleMarketStatusMutation.isPending}
            sx={{
              backgroundColor: '#d32f2f',
              color: '#FFFFFF',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#b71c1c' }
            }}
          >
            {toggleMarketStatusMutation.isPending ? 'Deactivating...' : 'Deactivate Market'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Market Image Dialog */}
      {market && (
        <GenerateMarketImageDialog
          open={generateImageDialogOpen}
          onClose={() => setGenerateImageDialogOpen(false)}
          marketId={marketId!}
          marketName={market.name}
          country={market.countryName}
          imageType={selectedImageType}
          onSuccess={handleImageGenerationSuccess}
        />
      )}
    </Container>
  );
};